import React, { useState, useRef, ReactNode, forwardRef } from 'react';
import {
  Button,
  Dropdown,
  Form,
  VerticalSpacing,
  getClassNameFactory,
  getRehydratableName,
  Typography,
  Toast,
  Sizes,
  FlexGrid,
  Modal,
  Heading,
  TextBoldness,
} from '@dcl/ui';
import { ToastType } from '@dcl/ui/dist/fragments/Toast';
import { ButtonKind, ButtonColor } from '@dcl/ui/dist/elements/Button';
import { types, boldTypes } from '@dcl/ui/dist/elements/Heading';
import { noop } from 'lodash';
import { TypographyTags } from '@dcl/ui/dist/elements/Typography';
import {
  ALT_EMAIL,
  ALT_PHONE,
  ALTERNATE_EMAIL,
  API_ERROR_MSG,
  BINDER_NAME,
  BINDER_NAME_NUMBER,
  CANCEL,
  CASE_API,
  CRITICAL_PROCCESS,
  DESCP,
  DESCRIPTION,
  EMAIL,
  EMAIL_LABEL,
  ENTITY_NAME,
  ENTITY_NAME_NUMBER,
  ESC_CRITICAL_PROCCESS,
  ESC_IMPACT,
  ESC_JURISDICTION,
  ESC_LOCATOR,
  ESC_PARTNER_ID,
  ESC_PERMISSION_COPY,
  ESC_PERMISSION_TRANSMIT,
  ESC_PERMISSION_UTILIZE,
  ESC_PRODUCT_BUG,
  ESC_TAX_FORM,
  ESC_TAX_TYPE,
  ESC_WORKAROUND,
  FIRST_NAME,
  FIRST_NAME_LABEL,
  HEADER_NAME,
  IMPACT,
  initialState,
  JURISDICTION,
  LAST_NAME,
  LAST_NAME_LABEL,
  LOCATOR,
  OAUTH_API,
  PAN,
  PAN_ACC,
  PARTNER_ID,
  PERMISSION_COPY,
  PERMISSION_TRANSMIT,
  PERMISSION_UTILIZE,
  PHONE,
  PREF_CONTACT,
  PREF_CONTACT_METHOD,
  PRODUCT_BUG,
  PRODUCT_SEGMENT,
  PRODUCT_TITLE,
  REQUIRED,
  SEGMENT_LABEL,
  SUB,
  SUBJECT,
  SUBMIT,
  TAX_FORM,
  TAX_TYPE,
  TAX_YEAR,
  TAX_YR,
  TITLE_LABEL,
  WORKAREA_NAME,
  WORKAREA_NAME_NUMBER,
  WORKAROUND,
} from './constants';
import ErrorIcon from './utils/ErrorIcon';
import { IDropdownItem } from '@dcl/ui/dist/modules/Dropdown';
import { mockData } from './components/CascadingDropdown/mock';
import { CascadingDropdown } from './components/CascadingDropdown';
import { RelatedContent } from './components/RelatedContent';
import { validateField, validateForm, validators } from './helper';
import { SubmitModal } from './components/SubmitModal/SubmitModal';
import { IStaticForm } from './types';
import AwsCaptcha from './components/AwsCaptcha/AwsCaptcha';
import './index.css';

export const StaticForm: React.FC<IStaticForm> = forwardRef((props) => {
  const getClassName = getClassNameFactory(StaticForm.displayName);
  const { clientId, clientSecret } = props;

  const [formState, setFormState] = useState(initialState);
  const [apiError, setApiError] = useState<boolean>(false);
  const [formSubmit, setFormSubmit] = useState<boolean>(false);
  const [captchaToken, setCaptchaToken] = useState<string>('');

  const handleChange = (
    formInput: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { id, value } = formInput.target;
    const ariaRequired = formInput.target.getAttribute('aria-required');

    let error = '';
    let required = false;

    if (ariaRequired === 'true') {
      required = true;
    }

    if (ariaRequired && !value) {
      error = `Enter an ${id}.`;
    }
    error = validateField(id as keyof typeof validators, value);

    setFormState({
      ...formState,
      [id]: { value, error, required },
    });
  };

  const handleCheckBoxChange = (
    formInput: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { id, value } = formInput.target;
    const checked = value !== 'true';

    setFormState({
      ...formState,
      [id]: { value: checked, error: '' },
    });
  };

  const handleSelectChange = (name: string, selectValue: string) => {
    setFormState({
      ...formState,
      [name]: { value: selectValue, error: '' },
    });
  };

  const cascadingDropdownRef = useRef<{ getSelectedValues: () => any } | null>(
    null
  );

  const getCascadeDropdownSelectedValues = () => {
    if (cascadingDropdownRef.current) {
      return cascadingDropdownRef.current.getSelectedValues();
    }
  };

  const getFormState = () => {
    if (!validateForm(formState, setFormState)) {
      return null;
    }

    const payload: Record<string, any> = {};

    Object.keys(formState).forEach(
      (key) => (payload[key] = (formState as any)[key].value)
    );

    const updatedState = {
      ...payload,
      ...getCascadeDropdownSelectedValues(),
    };

    return updatedState;
  };

  const handleSubmit = async () => {
    const mandatoryFields = {
      reasonCode: 'CB_Reason33',
      currentFormUrl: '/en-us/account/support/submit-ticket',
    };

    const currentState = getFormState();

    if (!currentState) {
      setApiError(true);
    } else if (!captchaToken) {
      alert('Please complete the CAPTCHA');
      return;
    } else {
      const finalPayload = { ...currentState, ...mandatoryFields };

      /*-----------------AUTHENTICATION-----------*/
      const oauthCred = {
        grant_type: 'client_credentials',
        client_id: clientId,
        client_secret: clientSecret,
      };

      const formBody = new URLSearchParams(oauthCred).toString();

      try {
        const oauthApiRes = await fetch(OAUTH_API, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: formBody,
        });

        if (!oauthApiRes.ok) {
          setApiError(true);
          throw new Error(`OAuth API Error: ${oauthApiRes.statusText}`);
        }

        const oauthApiData = await oauthApiRes.json();
        const token = oauthApiData.access_token;

        if (!token) {
          setApiError(true);
          throw new Error('Token not found in response');
        }

        /*-----------------PAYLOAD-----------*/
        const caseCreateRes = await fetch(CASE_API, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(finalPayload),
        });

        if (!caseCreateRes.ok) {
          setApiError(true);
          throw new Error(`OAuth API Error: ${caseCreateRes.statusText}`);
        } else {
          const caseCreateApiData = await caseCreateRes.json();
          console.log('case created', caseCreateApiData);
          setApiError(false);
          setFormSubmit(true);
        }
      } catch (error) {
        console.error('Error', error);
        setApiError(true);
      }
    }
  };

  const filterFn = (
    items: IDropdownItem[],
    value: ReactNode
  ): IDropdownItem[] => {
    if (typeof value !== 'string') {
      return [];
    }

    const label = value.toLowerCase();

    return items.filter((item) => item.label.toLowerCase().includes(label));
  };

  React.useEffect(() => {
    const element = document.getElementById(DESCP);
    if (formState[DESCP].error && element) {
      element.classList.add(getClassName({ descendantName: 'descpError' }));
    } else {
      element &&
        element.classList.remove(
          getClassName({ descendantName: 'descpError' })
        );
    }
  }, [formState, getClassName]);

  return (
    <div
      className={getClassName()}
      data-rehydratable={getRehydratableName(StaticForm.displayName)}
    >
      <Modal
        closeThisComponent={noop}
        uncloseable={true}
        className={getClassName()}
      >
        {formSubmit ? (
          <SubmitModal />
        ) : (
          <>
            {' '}
            <Heading
              type={types.Medium}
              boldType={boldTypes.medium}
              data-testid="test-title"
              className={getClassName({
                descendantName: 'title',
              })}
            >
              {HEADER_NAME}
            </Heading>
            <div className={getClassName({ descendantName: 'subTitle' })}>
              <div className={getClassName({ descendantName: 'rowContainer' })}>
                <Typography
                  type={Sizes.XXTRASMALL}
                  tag={TypographyTags.P}
                  boldness={TextBoldness.Medium}
                >
                  {SEGMENT_LABEL}
                </Typography>
                <Typography
                  type={Sizes.XXTRASMALL}
                  tag={TypographyTags.P}
                  boldness={TextBoldness.Medium}
                >
                  {PRODUCT_SEGMENT}
                </Typography>
              </div>
              <div className={getClassName({ descendantName: 'rowContainer' })}>
                <Typography
                  type={Sizes.XXTRASMALL}
                  tag={TypographyTags.P}
                  boldness={TextBoldness.Medium}
                >
                  {TITLE_LABEL}
                </Typography>
                <Typography
                  type={Sizes.XXTRASMALL}
                  tag={TypographyTags.P}
                  boldness={TextBoldness.Medium}
                >
                  {PRODUCT_TITLE}
                </Typography>
              </div>
            </div>
            {apiError && (
              <Toast type={ToastType.WARNING}>{API_ERROR_MSG}</Toast>
            )}
            <div className={getClassName({ descendantName: 'required' })}>
              <p>{REQUIRED}</p>
            </div>
            <div className={getClassName({ descendantName: 'section' })}>
              <VerticalSpacing>
                <Form.TextControl
                  id={SUB}
                  labelText={SUBJECT}
                  required
                  value={formState[SUB].value}
                  onChange={handleChange}
                  isError={Boolean(formState[SUB].error)}
                />
                {Boolean(formState[SUB].error) && (
                  <div className={getClassName({ descendantName: 'errorMsg' })}>
                    <div
                      className={getClassName({ descendantName: 'errorIcon' })}
                    >
                      <ErrorIcon />
                    </div>

                    <Typography type={Sizes.XXXTRASMALL} tag={TypographyTags.P}>
                      {formState[SUB].error}
                    </Typography>
                  </div>
                )}
              </VerticalSpacing>

              {/* -------------------------------Related content------------------------ */}

              <RelatedContent />

              <VerticalSpacing>
                <Form.TextControl
                  id={PAN_ACC}
                  labelText={PAN}
                  required
                  value={formState[PAN_ACC].value}
                  onChange={handleChange}
                  isError={Boolean(formState[PAN_ACC].error)}
                />
                {Boolean(formState[PAN_ACC].error) && (
                  <div className={getClassName({ descendantName: 'errorMsg' })}>
                    <div
                      className={getClassName({ descendantName: 'errorIcon' })}
                    >
                      <ErrorIcon />
                    </div>

                    <Typography type={Sizes.XXXTRASMALL} tag={TypographyTags.P}>
                      {formState[PAN_ACC].error}
                    </Typography>
                  </div>
                )}
              </VerticalSpacing>
              <VerticalSpacing>
                <label htmlFor={PREF_CONTACT_METHOD}>
                  <Typography
                    className={getClassName({
                      descendantName: 'dropdownLabel',
                    })}
                  >
                    {PREF_CONTACT}
                  </Typography>
                </label>
                <Dropdown
                  className={
                    formState[PREF_CONTACT_METHOD].error
                      ? getClassName({
                          descendantName: 'selectDropdown',
                          modifiers: 'descpError',
                        })
                      : getClassName({ descendantName: 'selectDropdown' })
                  }
                  filterFunction={filterFn}
                  typeaheadMode
                  maxVisibleItems={5}
                  block={true}
                  items={[{ label: '--None--', value: 'NA' }]}
                  onValueChanged={(selectValue: ReactNode) =>
                    handleSelectChange(
                      PREF_CONTACT_METHOD,
                      selectValue as string
                    )
                  }
                  onClearButtonClick={() =>
                    handleSelectChange(PREF_CONTACT_METHOD, '')
                  }
                  placeholder="--None--"
                  removeAutoFocus
                />
                {Boolean(formState[PREF_CONTACT_METHOD].error) && (
                  <div className={getClassName({ descendantName: 'errorMsg' })}>
                    <div
                      className={getClassName({ descendantName: 'errorIcon' })}
                    >
                      <ErrorIcon />
                    </div>

                    <Typography type={Sizes.XXXTRASMALL} tag={TypographyTags.P}>
                      {formState[PREF_CONTACT_METHOD].error}
                    </Typography>
                  </div>
                )}
              </VerticalSpacing>
              <VerticalSpacing>
                <label htmlFor={ESC_IMPACT}>
                  <Typography
                    className={getClassName({
                      descendantName: 'dropdownLabel',
                    })}
                  >
                    {IMPACT}
                  </Typography>
                </label>
                <Dropdown
                  className={
                    formState[ESC_IMPACT].error
                      ? getClassName({
                          descendantName: 'selectDropdown',
                          modifiers: 'descpError',
                        })
                      : getClassName({ descendantName: 'selectDropdown' })
                  }
                  filterFunction={filterFn}
                  typeaheadMode
                  maxVisibleItems={5}
                  block={true}
                  items={[{ label: '--None--', value: 'NA' }]}
                  onValueChanged={(selectValue: ReactNode) =>
                    handleSelectChange(ESC_IMPACT, selectValue as string)
                  }
                  onClearButtonClick={() => handleSelectChange(ESC_IMPACT, '')}
                  placeholder="--None--"
                  removeAutoFocus
                />
                {Boolean(formState[ESC_IMPACT].error) && (
                  <div className={getClassName({ descendantName: 'errorMsg' })}>
                    <div
                      className={getClassName({ descendantName: 'errorIcon' })}
                    >
                      <ErrorIcon />
                    </div>

                    <Typography type={Sizes.XXXTRASMALL} tag={TypographyTags.P}>
                      {formState[ESC_IMPACT].error}
                    </Typography>
                  </div>
                )}
              </VerticalSpacing>
              <VerticalSpacing>
                <Form.TextControl
                  id={FIRST_NAME}
                  labelText={FIRST_NAME_LABEL}
                  required
                  value={formState[FIRST_NAME].value}
                  onChange={handleChange}
                  isError={Boolean(formState[FIRST_NAME].error)}
                />
                {Boolean(formState[FIRST_NAME].error) && (
                  <div className={getClassName({ descendantName: 'errorMsg' })}>
                    <div
                      className={getClassName({ descendantName: 'errorIcon' })}
                    >
                      <ErrorIcon />
                    </div>

                    <Typography type={Sizes.XXXTRASMALL} tag={TypographyTags.P}>
                      {formState[FIRST_NAME].error}
                    </Typography>
                  </div>
                )}
              </VerticalSpacing>
              <VerticalSpacing>
                <Form.TextControl
                  id={LAST_NAME}
                  labelText={LAST_NAME_LABEL}
                  required
                  value={formState[LAST_NAME].value}
                  onChange={handleChange}
                  isError={Boolean(formState[LAST_NAME].error)}
                />
                {Boolean(formState[LAST_NAME].error) && (
                  <div className={getClassName({ descendantName: 'errorMsg' })}>
                    <div
                      className={getClassName({ descendantName: 'errorIcon' })}
                    >
                      <ErrorIcon />
                    </div>

                    <Typography type={Sizes.XXXTRASMALL} tag={TypographyTags.P}>
                      {formState[LAST_NAME].error}
                    </Typography>
                  </div>
                )}
              </VerticalSpacing>
              <VerticalSpacing>
                <Form.TextControl
                  id={EMAIL}
                  labelText={EMAIL_LABEL}
                  required
                  value={formState[EMAIL].value}
                  onChange={handleChange}
                  isError={Boolean(formState[EMAIL].error)}
                />
                {Boolean(formState[EMAIL].error) && (
                  <div className={getClassName({ descendantName: 'errorMsg' })}>
                    <div
                      className={getClassName({ descendantName: 'errorIcon' })}
                    >
                      <ErrorIcon />
                    </div>

                    <Typography type={Sizes.XXXTRASMALL} tag={TypographyTags.P}>
                      {formState[EMAIL].error}
                    </Typography>
                  </div>
                )}
              </VerticalSpacing>
              <VerticalSpacing>
                <Form.TextControl
                  id={ALTERNATE_EMAIL}
                  labelText={ALT_EMAIL}
                  value={formState[ALTERNATE_EMAIL].value}
                  onChange={handleChange}
                  isError={Boolean(formState[ALTERNATE_EMAIL].error)}
                />
                {Boolean(formState[ALTERNATE_EMAIL].error) && (
                  <div className={getClassName({ descendantName: 'errorMsg' })}>
                    <div
                      className={getClassName({ descendantName: 'errorIcon' })}
                    >
                      <ErrorIcon />
                    </div>

                    <Typography type={Sizes.XXXTRASMALL} tag={TypographyTags.P}>
                      {formState[ALTERNATE_EMAIL].error}
                    </Typography>
                  </div>
                )}
              </VerticalSpacing>
              <VerticalSpacing>
                <Form.TextControl
                  id={PHONE}
                  labelText={ALT_PHONE}
                  required
                  value={formState[PHONE].value}
                  onChange={handleChange}
                  isError={Boolean(formState[PHONE].error)}
                />
                {Boolean(formState[PHONE].error) && (
                  <div className={getClassName({ descendantName: 'errorMsg' })}>
                    <div
                      className={getClassName({ descendantName: 'errorIcon' })}
                    >
                      <ErrorIcon />
                    </div>

                    <Typography type={Sizes.XXXTRASMALL} tag={TypographyTags.P}>
                      {formState[PHONE].error}
                    </Typography>
                  </div>
                )}
              </VerticalSpacing>

              <VerticalSpacing>
                <Form.TextareaControl
                  id={DESCP}
                  labelText={DESCRIPTION}
                  required
                  value={formState[DESCP].value}
                  onChange={handleChange}
                />
                {Boolean(formState[DESCP].error) && (
                  <div className={getClassName({ descendantName: 'errorMsg' })}>
                    <div
                      className={getClassName({ descendantName: 'errorIcon' })}
                    >
                      <ErrorIcon />
                    </div>

                    <Typography type={Sizes.XXXTRASMALL} tag={TypographyTags.P}>
                      {formState[DESCP].error}
                    </Typography>
                  </div>
                )}
              </VerticalSpacing>
              {/*-------------------------------PickList-----------------------------*/}
              <VerticalSpacing>
                <CascadingDropdown
                  ref={cascadingDropdownRef}
                  dependentDropdowns={mockData.dependentDropdowns}
                  columns={1}
                />
              </VerticalSpacing>

              {/*-------------------------------2nd Row-----------------------------*/}

              <VerticalSpacing>
                <Form.TextControl
                  id={ESC_LOCATOR}
                  labelText={LOCATOR}
                  value={formState[ESC_LOCATOR].value}
                  onChange={handleChange}
                  isError={Boolean(formState[ESC_LOCATOR].error)}
                />
                {Boolean(formState[ESC_LOCATOR].error) && (
                  <div className={getClassName({ descendantName: 'errorMsg' })}>
                    <div
                      className={getClassName({ descendantName: 'errorIcon' })}
                    >
                      <ErrorIcon />
                    </div>

                    <Typography type={Sizes.XXXTRASMALL} tag={TypographyTags.P}>
                      {formState[ESC_LOCATOR].error}
                    </Typography>
                  </div>
                )}
              </VerticalSpacing>
              <VerticalSpacing>
                <label htmlFor={TAX_YEAR}>
                  <Typography
                    className={getClassName({
                      descendantName: 'dropdownLabel',
                    })}
                  >
                    {TAX_YR}
                  </Typography>
                </label>
                <Dropdown
                  className={
                    formState[TAX_YEAR].error
                      ? getClassName({
                          descendantName: 'selectDropdown',
                          modifiers: 'descpError',
                        })
                      : getClassName({ descendantName: 'selectDropdown' })
                  }
                  filterFunction={filterFn}
                  typeaheadMode
                  maxVisibleItems={5}
                  block={true}
                  items={[{ label: '--None--', value: 'NA' }]}
                  onValueChanged={(selectValue: ReactNode) =>
                    handleSelectChange(TAX_YEAR, selectValue as string)
                  }
                  onClearButtonClick={() => handleSelectChange(TAX_YEAR, '')}
                  placeholder="--None--"
                  removeAutoFocus
                />
                {Boolean(formState[TAX_YEAR].error) && (
                  <div className={getClassName({ descendantName: 'errorMsg' })}>
                    <div
                      className={getClassName({ descendantName: 'errorIcon' })}
                    >
                      <ErrorIcon />
                    </div>

                    <Typography type={Sizes.XXXTRASMALL} tag={TypographyTags.P}>
                      {formState[TAX_YEAR].error}
                    </Typography>
                  </div>
                )}
              </VerticalSpacing>
              <VerticalSpacing>
                <label htmlFor={ESC_TAX_TYPE}>
                  <Typography
                    className={getClassName({
                      descendantName: 'dropdownLabel',
                    })}
                  >
                    {TAX_TYPE}
                  </Typography>
                </label>
                <Dropdown
                  className={
                    formState[ESC_TAX_TYPE].error
                      ? getClassName({
                          descendantName: 'selectDropdown',
                          modifiers: 'descpError',
                        })
                      : getClassName({ descendantName: 'selectDropdown' })
                  }
                  filterFunction={filterFn}
                  typeaheadMode
                  maxVisibleItems={5}
                  block={true}
                  items={[{ label: '--None--', value: 'NA' }]}
                  onValueChanged={(selectValue: ReactNode) =>
                    handleSelectChange(ESC_TAX_TYPE, selectValue as string)
                  }
                  onClearButtonClick={() =>
                    handleSelectChange(ESC_TAX_TYPE, '')
                  }
                  placeholder="--None--"
                  removeAutoFocus
                />
                {Boolean(formState[ESC_TAX_TYPE].error) && (
                  <div className={getClassName({ descendantName: 'errorMsg' })}>
                    <div
                      className={getClassName({ descendantName: 'errorIcon' })}
                    >
                      <ErrorIcon />
                    </div>

                    <Typography type={Sizes.XXXTRASMALL} tag={TypographyTags.P}>
                      {formState[ESC_TAX_TYPE].error}
                    </Typography>
                  </div>
                )}
              </VerticalSpacing>
              <VerticalSpacing>
                <label htmlFor={ESC_JURISDICTION}>
                  <Typography
                    className={getClassName({
                      descendantName: 'dropdownLabel',
                    })}
                  >
                    {JURISDICTION}
                  </Typography>
                </label>
                <Dropdown
                  className={
                    formState[ESC_JURISDICTION].error
                      ? getClassName({
                          descendantName: 'selectDropdown',
                          modifiers: 'descpError',
                        })
                      : getClassName({ descendantName: 'selectDropdown' })
                  }
                  filterFunction={filterFn}
                  typeaheadMode
                  maxVisibleItems={5}
                  block={true}
                  items={[{ label: '--None--', value: 'NA' }]}
                  onValueChanged={(selectValue: ReactNode) =>
                    handleSelectChange(ESC_JURISDICTION, selectValue as string)
                  }
                  onClearButtonClick={() =>
                    handleSelectChange(ESC_JURISDICTION, '')
                  }
                  placeholder="--None--"
                  removeAutoFocus
                />
                {Boolean(formState[ESC_JURISDICTION].error) && (
                  <div className={getClassName({ descendantName: 'errorMsg' })}>
                    <div
                      className={getClassName({ descendantName: 'errorIcon' })}
                    >
                      <ErrorIcon />
                    </div>

                    <Typography type={Sizes.XXXTRASMALL} tag={TypographyTags.P}>
                      {formState[ESC_JURISDICTION].error}
                    </Typography>
                  </div>
                )}
              </VerticalSpacing>
              <VerticalSpacing>
                <label htmlFor={ESC_TAX_FORM}>
                  <Typography
                    className={getClassName({
                      descendantName: 'dropdownLabel',
                    })}
                  >
                    {TAX_FORM}
                  </Typography>
                </label>
                <Dropdown
                  className={
                    formState[ESC_TAX_FORM].error
                      ? getClassName({
                          descendantName: 'selectDropdown',
                          modifiers: 'descpError',
                        })
                      : getClassName({ descendantName: 'selectDropdown' })
                  }
                  filterFunction={filterFn}
                  typeaheadMode
                  maxVisibleItems={5}
                  block={true}
                  items={[{ label: '--None--', value: 'NA' }]}
                  onValueChanged={(selectValue: ReactNode) =>
                    handleSelectChange(ESC_TAX_FORM, selectValue as string)
                  }
                  onClearButtonClick={() =>
                    handleSelectChange(ESC_TAX_FORM, '')
                  }
                  placeholder="--None--"
                  removeAutoFocus
                />
                {Boolean(formState[ESC_TAX_FORM].error) && (
                  <div className={getClassName({ descendantName: 'errorMsg' })}>
                    <div
                      className={getClassName({ descendantName: 'errorIcon' })}
                    >
                      <ErrorIcon />
                    </div>

                    <Typography type={Sizes.XXXTRASMALL} tag={TypographyTags.P}>
                      {formState[ESC_TAX_FORM].error}
                    </Typography>
                  </div>
                )}
              </VerticalSpacing>
              <VerticalSpacing>
                <Form.TextControl
                  id={ENTITY_NAME_NUMBER}
                  labelText={ENTITY_NAME}
                  value={formState[ENTITY_NAME_NUMBER].value}
                  onChange={handleChange}
                  isError={Boolean(formState[ENTITY_NAME_NUMBER].error)}
                />
                {Boolean(formState[ENTITY_NAME_NUMBER].error) && (
                  <div className={getClassName({ descendantName: 'errorMsg' })}>
                    <div
                      className={getClassName({ descendantName: 'errorIcon' })}
                    >
                      <ErrorIcon />
                    </div>

                    <Typography type={Sizes.XXXTRASMALL} tag={TypographyTags.P}>
                      {formState[ENTITY_NAME_NUMBER].error}
                    </Typography>
                  </div>
                )}
              </VerticalSpacing>
              <VerticalSpacing>
                <Form.TextControl
                  id={WORKAREA_NAME_NUMBER}
                  labelText={WORKAREA_NAME}
                  value={formState[WORKAREA_NAME_NUMBER].value}
                  onChange={handleChange}
                  isError={Boolean(formState[WORKAREA_NAME_NUMBER].error)}
                />
                {Boolean(formState[WORKAREA_NAME_NUMBER].error) && (
                  <div className={getClassName({ descendantName: 'errorMsg' })}>
                    <div
                      className={getClassName({ descendantName: 'errorIcon' })}
                    >
                      <ErrorIcon />
                    </div>

                    <Typography type={Sizes.XXXTRASMALL} tag={TypographyTags.P}>
                      {formState[WORKAREA_NAME_NUMBER].error}
                    </Typography>
                  </div>
                )}
              </VerticalSpacing>
              <VerticalSpacing>
                <Form.TextControl
                  id={BINDER_NAME_NUMBER}
                  labelText={BINDER_NAME}
                  value={formState[BINDER_NAME_NUMBER].value}
                  onChange={handleChange}
                  isError={Boolean(formState[BINDER_NAME_NUMBER].error)}
                />
                {Boolean(formState[BINDER_NAME_NUMBER].error) && (
                  <div className={getClassName({ descendantName: 'errorMsg' })}>
                    <div
                      className={getClassName({ descendantName: 'errorIcon' })}
                    >
                      <ErrorIcon />
                    </div>

                    <Typography type={Sizes.XXXTRASMALL} tag={TypographyTags.P}>
                      {formState[BINDER_NAME_NUMBER].error}
                    </Typography>
                  </div>
                )}
              </VerticalSpacing>
              <VerticalSpacing>
                <Form.CheckboxControl
                  id={ESC_PERMISSION_COPY}
                  labelText={PERMISSION_COPY}
                  onChange={handleCheckBoxChange}
                  value={formState[ESC_PERMISSION_COPY].value}
                />
                {Boolean(formState[ESC_PERMISSION_COPY].error) && (
                  <div className={getClassName({ descendantName: 'errorMsg' })}>
                    <div
                      className={getClassName({ descendantName: 'errorIcon' })}
                    >
                      <ErrorIcon />
                    </div>

                    <Typography type={Sizes.XXXTRASMALL} tag={TypographyTags.P}>
                      {formState[ESC_PERMISSION_COPY].error}
                    </Typography>
                  </div>
                )}
              </VerticalSpacing>
              <VerticalSpacing>
                <Form.CheckboxControl
                  id={ESC_PERMISSION_UTILIZE}
                  labelText={PERMISSION_UTILIZE}
                  onChange={handleCheckBoxChange}
                  value={formState[ESC_PERMISSION_UTILIZE].value}
                />
                {Boolean(formState[ESC_PERMISSION_UTILIZE].error) && (
                  <div className={getClassName({ descendantName: 'errorMsg' })}>
                    <div
                      className={getClassName({ descendantName: 'errorIcon' })}
                    >
                      <ErrorIcon />
                    </div>

                    <Typography type={Sizes.XXXTRASMALL} tag={TypographyTags.P}>
                      {formState[ESC_PERMISSION_UTILIZE].error}
                    </Typography>
                  </div>
                )}
              </VerticalSpacing>
              <VerticalSpacing>
                <Form.TextControl
                  id={ESC_PARTNER_ID}
                  labelText={PARTNER_ID}
                  value={formState[ESC_PARTNER_ID].value}
                  onChange={handleChange}
                  isError={Boolean(formState[ESC_PARTNER_ID].error)}
                />
                {Boolean(formState[ESC_PARTNER_ID].error) && (
                  <div className={getClassName({ descendantName: 'errorMsg' })}>
                    <div
                      className={getClassName({ descendantName: 'errorIcon' })}
                    >
                      <ErrorIcon />
                    </div>

                    <Typography type={Sizes.XXXTRASMALL} tag={TypographyTags.P}>
                      {formState[ESC_PARTNER_ID].error}
                    </Typography>
                  </div>
                )}
              </VerticalSpacing>
              <VerticalSpacing>
                <Form.CheckboxControl
                  id={ESC_PERMISSION_TRANSMIT}
                  labelText={PERMISSION_TRANSMIT}
                  onChange={handleCheckBoxChange}
                  value={formState[ESC_PERMISSION_TRANSMIT].value}
                />
                {Boolean(formState[ESC_PERMISSION_TRANSMIT].error) && (
                  <div className={getClassName({ descendantName: 'errorMsg' })}>
                    <div
                      className={getClassName({ descendantName: 'errorIcon' })}
                    >
                      <ErrorIcon />
                    </div>

                    <Typography type={Sizes.XXXTRASMALL} tag={TypographyTags.P}>
                      {formState[ESC_PERMISSION_TRANSMIT].error}
                    </Typography>
                  </div>
                )}
              </VerticalSpacing>
              <VerticalSpacing>
                <Form.CheckboxControl
                  id={ESC_PRODUCT_BUG}
                  labelText={PRODUCT_BUG}
                  onChange={handleCheckBoxChange}
                  value={formState[ESC_PRODUCT_BUG].value}
                />
                {Boolean(formState[ESC_PRODUCT_BUG].error) && (
                  <div className={getClassName({ descendantName: 'errorMsg' })}>
                    <div
                      className={getClassName({ descendantName: 'errorIcon' })}
                    >
                      <ErrorIcon />
                    </div>

                    <Typography type={Sizes.XXXTRASMALL} tag={TypographyTags.P}>
                      {formState[ESC_PRODUCT_BUG].error}
                    </Typography>
                  </div>
                )}
              </VerticalSpacing>
              <VerticalSpacing>
                <Form.CheckboxControl
                  id={ESC_CRITICAL_PROCCESS}
                  labelText={CRITICAL_PROCCESS}
                  onChange={handleCheckBoxChange}
                  value={formState[ESC_CRITICAL_PROCCESS].value}
                />
                {Boolean(formState[ESC_CRITICAL_PROCCESS].error) && (
                  <div className={getClassName({ descendantName: 'errorMsg' })}>
                    <div
                      className={getClassName({ descendantName: 'errorIcon' })}
                    >
                      <ErrorIcon />
                    </div>

                    <Typography type={Sizes.XXXTRASMALL} tag={TypographyTags.P}>
                      {formState[ESC_CRITICAL_PROCCESS].error}
                    </Typography>
                  </div>
                )}
              </VerticalSpacing>
              <VerticalSpacing>
                <Form.CheckboxControl
                  id={ESC_WORKAROUND}
                  labelText={WORKAROUND}
                  onChange={handleCheckBoxChange}
                  value={formState[ESC_WORKAROUND].value}
                />
                {Boolean(formState[ESC_WORKAROUND].error) && (
                  <div className={getClassName({ descendantName: 'errorMsg' })}>
                    <div
                      className={getClassName({ descendantName: 'errorIcon' })}
                    >
                      <ErrorIcon />
                    </div>

                    <Typography type={Sizes.XXXTRASMALL} tag={TypographyTags.P}>
                      {formState[ESC_WORKAROUND].error}
                    </Typography>
                  </div>
                )}
              </VerticalSpacing>
            </div>
            <AwsCaptcha onVerify={setCaptchaToken} />
            <FlexGrid>
              <FlexGrid.Row>
                <FlexGrid.Col>
                  <Button
                    kind={ButtonKind.BUTTON}
                    color={ButtonColor.SECONDARY}
                    disabled={false}
                    onClick={() => {}}
                  >
                    {CANCEL}
                  </Button>
                  <Button
                    kind={ButtonKind.BUTTON}
                    color={ButtonColor.PRIMARY}
                    disabled={false}
                    onClick={handleSubmit}
                  >
                    {SUBMIT}
                  </Button>
                </FlexGrid.Col>
              </FlexGrid.Row>
            </FlexGrid>
          </>
        )}
      </Modal>
    </div>
  );
});

StaticForm.displayName = 'StaticForm';
